import { getJson, sendJson } from '@/lib/api';

/** The agreement of record for a client (§46), as the admin API returns it. */
export interface Agreement {
  readonly _id: string;
  readonly organizationId: string;
  readonly status: 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  readonly termStart: string;
  readonly termEnd: string;
  readonly creditsPurchased: number;
  readonly expirationPolicy: 'EXPIRE' | 'CARRY_FORWARD' | 'CARRY_FORWARD_CAPPED';
  readonly carryForwardCap?: number | null;
  readonly renewalTerms?: string | null;
  readonly cancellationTerms?: string | null;
  readonly additionalCreditPrice?: number | null;
  /** Rolls into a new term at the same credits when this one ends. */
  readonly autoRenew?: boolean;
  readonly unitPrice?: number | null;
  readonly discountPercent?: number;
  readonly billingOffice?: string | null;
  readonly accountsPayableName?: string | null;
  readonly accountsPayableEmail?: string | null;
  readonly purchaseOrderRequired: boolean;
  readonly purchaseOrderNumber?: string | null;
  readonly taxExempt: boolean;
  readonly bceFinancialContact?: string | null;
  readonly clientExecSponsor?: string | null;
  readonly clientContractAdmin?: string | null;
  readonly notes?: string | null;
  readonly createdAt: string;
}

/** The fields BCE writes; every one optional so a form can save one change. */
export type AgreementWrite = Partial<
  Omit<Agreement, '_id' | 'organizationId' | 'createdAt'>
>;

export type AgreementRequestKind = 'NEW' | 'RENEWAL' | 'ADD_ON';
export type AgreementRequestStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'DRAFTING'
  | 'IN_REVIEW'
  | 'READY_TO_SIGN'
  | 'SIGNED'
  | 'PAID'
  | 'DECLINED'
  | 'CANCEL_REQUESTED'
  | 'CANCELED';

/** A client's ask for a package, a renewal or more credits (§46). */
export interface AgreementRequest {
  readonly _id: string;
  readonly organizationId: string;
  readonly kind: AgreementRequestKind;
  readonly status: AgreementRequestStatus;
  readonly credits: number;
  readonly unitPrice: number;
  readonly discountPercent: number;
  readonly total: number;
  /** A renewal that lowers the credits: BCE approves it before the client can pay. */
  readonly requiresApproval?: boolean;
  readonly termMonths: number;
  readonly termStart?: string;
  readonly termEnd?: string;
  readonly requestedByName?: string;
  readonly note?: string;
  /** Credits the client changed after filing, oldest first. */
  readonly changeRequests?: readonly { readonly at: string; readonly byName: string; readonly from: number; readonly credits: number }[];
  readonly contractId?: string;
  readonly agreementId?: string;
  readonly approvedAt?: string;
  readonly approvedByName?: string;
  readonly paidAt?: string;
  readonly amountPaid?: number;
  readonly statusBeforeCancel?: AgreementRequestStatus;
  readonly cancelRequestedAt?: string;
  readonly declinedReason?: string;
  readonly decidedByName?: string;
  readonly signedAt?: string;
  readonly createdAt: string;
}

export interface OrganizationAgreement {
  readonly agreement: Agreement | null;
  readonly history: readonly Agreement[];
  readonly requests: readonly AgreementRequest[];
}

export const REQUEST_KIND_LABELS: Readonly<Record<AgreementRequestKind, string>> = {
  NEW: 'New agreement',
  RENEWAL: 'Renewal',
  ADD_ON: 'Additional credits',
};

export const REQUEST_STATUS_LABELS: Readonly<Record<AgreementRequestStatus, string>> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  DRAFTING: 'Drafting',
  IN_REVIEW: 'With the client',
  READY_TO_SIGN: 'Out for signature',
  SIGNED: 'Signed',
  PAID: 'Paid — credits applied',
  DECLINED: 'Declined',
  CANCEL_REQUESTED: 'Wants to cancel',
  CANCELED: 'Canceled by the client',
};

export async function readOrganizationAgreement(organizationId: string, signal?: AbortSignal): Promise<OrganizationAgreement> {
  return getJson<OrganizationAgreement>(`/organizations/${organizationId}/agreement`, undefined, signal);
}
export async function saveOrganizationAgreement(organizationId: string, body: AgreementWrite): Promise<Agreement> {
  return sendJson<Agreement>('put', `/organizations/${organizationId}/agreement`, body);
}
export async function approveAgreementRequest(organizationId: string, requestId: string): Promise<AgreementRequest> {
  return sendJson<AgreementRequest>('post', `/organizations/${organizationId}/agreement-requests/${requestId}/approve`, {});
}

export async function decideCancellation(organizationId: string, requestId: string, approve: boolean): Promise<AgreementRequest> {
  return sendJson<AgreementRequest>('post', `/organizations/${organizationId}/agreement-requests/${requestId}/cancellation`, { approve });
}

export async function declineAgreementRequest(organizationId: string, requestId: string, reason: string): Promise<AgreementRequest> {
  return sendJson<AgreementRequest>('post', `/organizations/${organizationId}/agreement-requests/${requestId}/decline`, { reason });
}
